import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Plus, Search, Filter, DollarSign, Globe, Star } from 'lucide-react';
import { addProject } from '../store';
import { Project, ProjectStatus } from '../types';
import CreateProjectModal from '../components/CreateProjectModal';
import { getAllQuarterlyProjects, getCurrentQuarter } from '../utils/quarterlyProjects';
import {
  getProjectStatusClassName,
  matchesProjectStatus,
  normalizeProjectStatus,
} from '../utils/projectStatus';
import { canViewProjectFinance, isExternalPartnerUser } from '../utils/projectAccess';
import { usePreferences } from '../contexts/PreferencesContext';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import SurfaceCard from '../components/SurfaceCard';

export default function Projects() {
  /* ------------------------------------------------------------------ */
  /* SHARED STATE                                                       */
  /* ------------------------------------------------------------------ */

  
  const { state, loading, reload } = useApp();
    const [searchParams] = useSearchParams();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<ProjectStatus | 'All'>(
      (normalizeProjectStatus(searchParams.get('status')) || 'All') as ProjectStatus | 'All'
    );
  const { isFavoriteProject, toggleFavoriteProject } = usePreferences();

  /* ------------------------------------------------------------------ */
  /* REFRESH DATA ON MODAL CLOSE                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const userEmail = state?.currentUser?.email;
    console.log('[Projects] userEmail:', userEmail);
    if (!isCreateModalOpen && userEmail) {
      reload(userEmail);
    }
  }, [isCreateModalOpen, state?.currentUser?.email, reload]);

  useEffect(() => {
    const statusFromQuery = normalizeProjectStatus(searchParams.get('status'));
    setFilterStatus(statusFromQuery || 'All');
  }, [searchParams]);

  const handleCreateProject = async (project: Project) => {
    // 1. Send the data to the server
    await addProject(project); 
    // 2. Close the modal
    // The useEffect will detect this change and trigger the reload for you
    setIsCreateModalOpen(false); 
  };

  /* ------------------------------------------------------------------ */
  /* SAFE DATA (prevents null crashes)                                  */
  /* ------------------------------------------------------------------ */

  const organizations = state?.organizations ?? [];
  const { projects, tasks, orgId } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    projects: Project[];
    tasks: any[];
    orgId?: string;
  };
  const currentOrgId = orgId;
  const isExternalPartner = isExternalPartnerUser(state?.currentUser);
  const assignedTaskCountByProject = useMemo(() => {
    const counts = new Map<string, number>();

    tasks.forEach((task) => {
      const projectKey = String(task.projectId || '');
      if (!projectKey) {
        return;
      }

      counts.set(projectKey, (counts.get(projectKey) || 0) + 1);
    });

    return counts;
  }, [tasks]);
  const createModalOrgId =
    currentOrgId || state?.currentOrganization?.id || state?.currentUser?.organizationId || '';

  /* ------------------------------------------------------------------ */
  /* FILTER LOGIC (unchanged)                                           */
  /* ------------------------------------------------------------------ */

  const filteredProjects = projects.filter((project: Project) => {
    const matchesSearch = isExternalPartner
      ? project.name.toLowerCase().includes(searchQuery.toLowerCase())
      : project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = matchesProjectStatus(project.status, filterStatus);

    if (isExternalPartner && (assignedTaskCountByProject.get(project.id) || 0) === 0) {
      return false;
    }

    return matchesSearch && matchesFilter;
  });

  const quarterlyProjects = state?.currentOrganization
    ? getAllQuarterlyProjects(projects, state.currentOrganization.id)
    : [];
  const canAccessQuarterlyExpenses = !isExternalPartner && quarterlyProjects.some((project) =>
    canViewProjectFinance(state?.currentUser, project)
  );

  const currentQuarter = getCurrentQuarter();

  const getShortOrganizationName = (name: string, slug?: string): string => {
    const cleanName = String(name || '').trim();
    if (!cleanName) {
      return 'ORG';
    }

    const words = cleanName
      .replace(/[._]/g, ' ')
      .split(/[\s/&-]+/)
      .filter(Boolean);

    if (words.length > 1) {
      return words
        .slice(0, 4)
        .map((word) => word.charAt(0).toUpperCase())
        .join('');
    }

    const compactSlug = String(slug || '')
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase();
    if (compactSlug) {
      return compactSlug.slice(0, 4);
    }

    return cleanName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 4) || 'ORG';
  };

  /* ------------------------------------------------------------------ */
  /* OPTIONAL: loading state (no UI change)                             */
  /* ------------------------------------------------------------------ */

  if (loading && projects.length === 0) {
    // keeps layout stable; no spinner added
    return <div className="max-w-7xl mx-auto px-4 pb-6 pt-3 text-slate-500 dark:text-slate-400 sm:px-6 lg:px-8 lg:pt-4">Loading...</div>;
  }

  /* ------------------------------------------------------------------ */
  /* UI — UNCHANGED                                                     */
  /* ------------------------------------------------------------------ */

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      {/* Organization Indicator - Only show if multiple orgs or no specific org context */}
      {currentOrgId && organizations.length > 1 && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50/70 px-3 py-2 dark:border-blue-800 dark:bg-blue-950/30">
          <div className="flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300">
            <Globe className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-300" />
            <p className="truncate">
              Viewing projects across {organizations.length} organizations • Active: {state?.currentOrganization?.name || currentOrgId}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="mb-1 text-xl font-semibold text-slate-800 dark:text-slate-100 sm:text-2xl">Projects</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {isExternalPartner
              ? 'View your assigned tasks by project'
              : 'Manage your project proposals and workflows'}
          </p>
        </div>
        <div className="flex gap-2">
          {quarterlyProjects.length > 0 && canAccessQuarterlyExpenses && (
            <Link to="/app/quarterly">
              <button className="inline-flex h-9 items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 text-sm font-medium text-amber-700 transition-colors hover:border-amber-300 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300 dark:hover:bg-amber-900/40">
                <DollarSign className="w-4 h-4" />
                {currentQuarter.label} Expenses
              </button>
            </Link>
          )}
          {!isExternalPartner && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 text-sm font-medium text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
            >
              <Plus className="w-4 h-4" />
              New Project
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <SurfaceCard className="mb-5 p-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-slate-400 dark:text-slate-500" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-700"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400 dark:text-slate-500" />
            <select
              aria-label="Filter projects by status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ProjectStatus | 'All')}
              className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-700"
            >
              <option value="All">All Status</option>
              <option value="Draft">Draft</option>
              <option value="Under Review">Under Review</option>
              <option value="Approved">Approved</option>
              <option value="Rejected">Rejected</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
            </select>
          </div>
        </div>
      </SurfaceCard>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <SurfaceCard className="p-12 text-center">
          <p className="mb-4 text-slate-500 dark:text-slate-400">
            {searchQuery || filterStatus !== 'All'
              ? 'No projects match your filters'
              : 'No projects yet'}
          </p>
          {!isExternalPartner && !searchQuery && filterStatus === 'All' && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:hover:bg-blue-900/50"
            >
              <Plus className="w-4 h-4" />
              Create Your First Project
            </button>
          )}
        </SurfaceCard>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project: Project) => {
            const projectOrgs = organizations.filter(
              (org) =>
                org.id === project.organizationId ||
                project.organizationIds?.includes(org.id)
            );
            const assignedTaskCount = assignedTaskCountByProject.get(project.id) || 0;
            const organizationBadges =
              projectOrgs.length > 0
                ? projectOrgs.map((org) => ({
                    id: org.id,
                    fullName: org.name,
                    shortName: getShortOrganizationName(org.name, org.slug),
                  }))
                : [
                    {
                      id: project.organizationId,
                      fullName: project.organizationId,
                      shortName: getShortOrganizationName(project.organizationId, project.organizationId),
                    },
                  ];
            const isFavorite = isFavoriteProject(project.id);
            const canViewFinance = canViewProjectFinance(state?.currentUser, project);
            const totalCost = (project.costs ?? []).reduce((sum: number, cost) => sum + cost.amount, 0);
            const projectLink = isExternalPartner
              ? `/app/projects/${project.id}/tasks`
              : `/app/projects/${project.id}`;

            return (
              <SurfaceCard
                key={project.id}
                className="relative group p-6 transition-shadow hover:shadow-md"
              >
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleFavoriteProject(project.id);
                  }}
                  aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted/40 transition-colors z-10"
                >
                  <Star
                    className={`w-5 h-5 ${
                      isFavorite
                        ? 'fill-current text-chart-5'
                        : 'text-muted-foreground group-hover:text-chart-5'
                    }`}
                  />
                </button>

                <Link to={projectLink} className="block">
                  <div className="flex items-start justify-between mb-4 pr-10">
                    <h3 className="flex-1 pr-2 text-xl dark:text-muted-foreground">{project.name}</h3>
                    {!isExternalPartner && (
                      <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${getProjectStatusClassName(project.status)}`}>
                        {normalizeProjectStatus(project.status) || project.status}
                      </span>
                    )}
                  </div>

                  {organizationBadges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {organizationBadges.map((org) => (
                        <span
                          key={org.id}
                          title={org.fullName}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs"
                        >
                          <Globe className="w-3 h-3" />
                          {org.shortName}
                        </span>
                      ))}
                    </div>
                  )}

                  {!isExternalPartner && (
                    <p className="mb-4 line-clamp-2 text-muted-foreground">
                      {project.description}
                    </p>
                  )}
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    {isExternalPartner ? (
                      <span>{assignedTaskCount} assigned tasks</span>
                    ) : (
                      <>
                        <span>{(project.teamMembers ?? []).length} team members</span>
                        <span>
                          {canViewFinance
                            ? totalCost > 0
                              ? `$${totalCost.toLocaleString()}`
                              : 'No costs'
                            : 'Restricted'}
                        </span>
                      </>
                    )}
                  </div>
                </Link>
              </SurfaceCard>
            );
          })}
        </div>
      )}

      {!isExternalPartner && isCreateModalOpen && createModalOrgId && (
        <CreateProjectModal
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={handleCreateProject}
          users={state?.users ?? []}
          organizationId={createModalOrgId}
        />
      )}
    </div>
  );
}
